/**
 * Player Self-Scoring Service
 *
 * Handles score submission, validation, and reconciliation
 * for player self-scoring feature.
 *
 * For player scoring (via SMS links), uses Cloud Functions to bypass
 * Firestore security rules (players are unauthenticated).
 * For director scoring, uses direct Firestore writes (authenticated).
 */

import { db } from './config';
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  onSnapshot
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { findMatchByToken, checkTokenExpiration } from '../utils/tokenUtils';
import { readBracketAsArray } from '../utils/bracketSchema';

// Initialize Firebase Functions
const functions = getFunctions();

// Cloud Function references for player scoring
const playerStartMatchFn = httpsCallable(functions, 'playerStartMatch');
const playerUpdateLiveScoreFn = httpsCallable(functions, 'playerUpdateLiveScore');
const playerSubmitScoreFn = httpsCallable(functions, 'playerSubmitScore');

/**
 * Build a schema-aware Firestore write payload that modifies a single match's
 * fields.
 *
 * Returns one of two shapes depending on what's safe to use:
 *  - `{ match, mode: 'update', updates }` — pass to `updateDoc`. Only used when
 *    the path doesn't cross a dotted dynamic key (i.e. non-instance writes).
 *  - `{ match, mode: 'merge', updates }` — pass to `setDoc(..., { merge: true })`.
 *    Required when the path includes an instance date, because Firestore would
 *    split dot-containing keys like "2026-05-26T19:00:00.000" as field separators
 *    if we used dot-notation updateDoc paths.
 *
 * For v2 (bracketMap) tournaments, only the changed fields of one match are
 * sent. For legacy bracket-array tournaments, the whole bracket gets rewritten.
 */
function buildMatchFieldUpdate(source, matchIndex, fieldChanges) {
  if (!source) return null;

  // v2 schema: bracketMap + matchOrder. Now that a recurring occurrence is its
  // own subcollection document, dotted `bracketMap.<id>.<field>` paths are safe
  // for both the root doc and the instance doc — no date key to split.
  if (source.bracketMap && typeof source.bracketMap === 'object') {
    const matchId = Array.isArray(source.matchOrder) ? source.matchOrder[matchIndex] : undefined;
    const match = matchId ? source.bracketMap[matchId] : undefined;
    if (!match) return null;
    const updates = {};
    for (const [field, value] of Object.entries(fieldChanges)) {
      updates[`bracketMap.${matchId}.${field}`] = value;
    }
    return { match, updates };
  }

  // Legacy bracket array — write the whole array back.
  const bracket = source.bracket;
  if (!Array.isArray(bracket)) return null;
  const match = bracket[matchIndex];
  if (!match) return null;
  const updatedBracket = [...bracket];
  updatedBracket[matchIndex] = { ...match, ...fieldChanges };
  return { match, updates: { bracket: updatedBracket } };
}

// Ref to the document that holds the bracket: the occurrence's subcollection doc
// for recurring tournaments, else the root tournament doc.
function bracketContainerRef(tournamentId, instanceDate) {
  return instanceDate
    ? doc(db, 'tournaments', tournamentId, 'instances', instanceDate)
    : doc(db, 'tournaments', tournamentId);
}

// Load the bracket container (instance doc or root tournament) + its ref.
async function loadBracketContainer(tournamentId, instanceDate) {
  const ref = bracketContainerRef(tournamentId, instanceDate);
  const snap = await getDoc(ref);
  return { ref, data: snap.exists() ? snap.data() : null };
}

/**
 * Apply a payload from `buildMatchFieldUpdate` to the bracket's container doc.
 */
async function applyMatchFieldUpdate(ref, built) {
  await updateDoc(ref, built.updates);
}

/**
 * Validates a scoring token and returns match data
 *
 * @param {string} token - The scoring token
 * @param {string} [tournamentId] - Optional tournament ID for direct lookup (more efficient)
 * @returns {Promise<{ valid: boolean, tournament?: object, match?: object, playerPosition?: string, error?: string }>}
 */
export async function validateScoringToken(token, tournamentId, instanceDate) {
  try {
    let tournamentsToCheck = [];

    // If tournamentId is provided, fetch only that tournament (efficient)
    if (tournamentId) {
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      const tournamentSnap = await getDoc(tournamentRef);
      if (tournamentSnap.exists()) {
        tournamentsToCheck = [{ id: tournamentSnap.id, ...tournamentSnap.data() }];
      } else {
        return { valid: false, error: 'Tournament not found' };
      }
    } else {
      // Fallback: search all tournaments (backwards compatibility)
      const tournamentsRef = collection(db, 'tournaments');
      const snapshot = await getDocs(tournamentsRef);
      tournamentsToCheck = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    }

    for (const tournament of tournamentsToCheck) {
      // Skip if tournament doesn't have self-scoring enabled
      if (!tournament.selfScoringEnabled) continue;

      // Check for bracket in multiple locations:
      // 1. Direct bracket on tournament (non-recurring)
      // 2. The occurrence's own subcollection doc (recurring) — fetched by the
      //    instanceDate carried on the scoring link. Public `get` is allowed by
      //    rules; we never `list` the subcollection (players can't).
      const bracketsToCheck = [];

      // Check direct bracket (handles both legacy bracket array and v2 bracketMap)
      const rootBracket = readBracketAsArray(tournament);
      if (rootBracket.length > 0) {
        bracketsToCheck.push({ bracket: rootBracket, instanceDate: null });
      }

      // Recurring: fetch the single occurrence document named by the link.
      if (instanceDate) {
        try {
          const instSnap = await getDoc(doc(db, 'tournaments', tournament.id, 'instances', instanceDate));
          if (instSnap.exists()) {
            const instanceBracket = readBracketAsArray(instSnap.data());
            if (instanceBracket.length > 0) {
              bracketsToCheck.push({ bracket: instanceBracket, instanceDate });
            }
          }
        } catch (e) { /* not accessible — fall through */ }
      }

      // Transitional fallback: legacy parent-map instances (pre-migration docs).
      if (tournament.instances) {
        for (const [legacyDate, instanceData] of Object.entries(tournament.instances)) {
          const instanceBracket = readBracketAsArray(instanceData);
          if (instanceBracket.length > 0) {
            bracketsToCheck.push({ bracket: instanceBracket, instanceDate: legacyDate });
          }
        }
      }

      if (bracketsToCheck.length === 0) continue;

      // Search each bracket for the token
      for (const { bracket, instanceDate } of bracketsToCheck) {
        const { match, playerPosition, matchIndex } = findMatchByToken(bracket, token);

        if (match) {
          // Check if token has expired
          const expiration = checkTokenExpiration(match);
          if (expiration.expired) {
            return { valid: false, error: expiration.reason };
          }

          return {
            valid: true,
            tournament: {
              id: tournament.id,
              title: tournament.title,
              winnersRaceLength: tournament.winnersRaceLength || tournament.raceLength || 5,
              losersRaceLength: tournament.losersRaceLength || tournament.raceLength || 3,
              format: tournament.format,
              instanceDate // Include instance date for recurring tournaments
            },
            match: {
              ...match,
              matchIndex,
              player1: match.p1?.name || match.player1,
              player2: match.p2?.name || match.player2
            },
            playerPosition
          };
        }
      }
    }

    // If we got here with a specific tournamentId, the tournament exists but token wasn't found
    // This likely means the tournament was reset/reshuffled and new tokens were generated
    if (tournamentId && tournamentsToCheck.length > 0) {
      const tournament = tournamentsToCheck[0];
      // Check if tournament has self-scoring enabled and has a bracket (meaning it was reset)
      if (tournament.selfScoringEnabled) {
        const hasBracket = readBracketAsArray(tournament).length > 0 ||
          (tournament.instances && Object.values(tournament.instances).some(inst => readBracketAsArray(inst).length > 0));
        if (hasBracket) {
          return {
            valid: false,
            error: 'Tournament was reset. Please check for a new SMS notification with your updated scoring link.'
          };
        }
      }
    }

    return { valid: false, error: 'Invalid or expired scoring link' };
  } catch (error) {
    console.error('Error validating scoring token:', error);
    return { valid: false, error: 'Error validating token' };
  }
}

/**
 * Submits a player's score for a match
 *
 * SIMPLIFIED FLOW: First player to submit immediately finalizes the match.
 * No opponent verification required - tournament director can adjust if needed.
 *
 * @param {object} params - Score submission parameters
 * @param {string} params.tournamentId - Tournament ID
 * @param {number} params.matchIndex - Match index in bracket
 * @param {string} params.playerPosition - 'p1' or 'p2'
 * @param {number[]} params.games - Array of game results (1 = p1 won, 2 = p2 won)
 * @param {object} params.finalScore - Final score { p1: number, p2: number }
 * @param {string} [params.instanceDate] - Instance date for recurring tournaments
 * @param {string} [params.token] - Player's scoring token (if present, uses Cloud Function)
 * @returns {Promise<{ success: boolean, status?: string, error?: string }>}
 */
export async function submitScore({
  tournamentId,
  matchIndex,
  playerPosition,
  games,
  finalScore,
  instanceDate,
  token
}) {
  try {
    // If token is provided, use Cloud Function (player scoring - unauthenticated)
    if (token) {
      const result = await playerSubmitScoreFn({
        tournamentId,
        matchIndex,
        token,
        games,
        finalScore,
        instanceDate
      });
      return result.data;
    }

    // Otherwise use direct Firestore write (director - authenticated)
    // Immediately finalize the match - first player to submit wins
    const finalizeResult = await finalizeMatchFromScoring(
      tournamentId,
      matchIndex,
      finalScore,
      games,
      instanceDate
    );

    if (finalizeResult.success) {
      return {
        success: true,
        status: 'finalized',
        finalScore,
        winner: finalizeResult.winner
      };
    } else {
      // Check if match was already completed (opponent submitted first)
      if (finalizeResult.error === 'Match already completed') {
        return {
          success: true,
          status: 'already_completed',
          error: 'Match was already scored by your opponent'
        };
      }
      return {
        success: false,
        error: finalizeResult.error
      };
    }
  } catch (error) {
    console.error('Error submitting score:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Updates live score for a match in progress
 * Called after each game is recorded so spectators can follow along
 *
 * @param {object} params - Update parameters
 * @param {string} params.tournamentId - Tournament ID
 * @param {number} params.matchIndex - Match index in bracket
 * @param {number[]} params.games - Array of game results (1 = p1 won, 2 = p2 won)
 * @param {object} params.currentScore - Current score { p1: number, p2: number }
 * @param {string} [params.instanceDate] - Instance date for recurring tournaments
 * @param {boolean} params.isP1 - Whether this is Player 1 updating (determines which fields to write)
 * @param {string} [params.token] - Player's scoring token (if present, uses Cloud Function)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function updateLiveScore({
  tournamentId,
  matchIndex,
  games,
  currentScore,
  instanceDate,
  isP1,
  token
}) {
  try {
    // If token is provided, use Cloud Function (player scoring - unauthenticated)
    if (token) {
      const result = await playerUpdateLiveScoreFn({
        tournamentId,
        matchIndex,
        token,
        games,
        currentScore,
        instanceDate
      });
      return result.data;
    }

    // Otherwise use direct Firestore write (director - authenticated)
    const { ref, data: source } = await loadBracketContainer(tournamentId, instanceDate);
    if (!source) {
      throw new Error('Tournament not found');
    }

    const liveGamesField = isP1 ? 'p1LiveGames' : 'p2LiveGames';
    const liveScoreField = isP1 ? 'p1LiveScore' : 'p2LiveScore';
    const built = buildMatchFieldUpdate(source, matchIndex, {
      [liveGamesField]: games,
      [liveScoreField]: currentScore,
      liveUpdatedAt: Date.now()
    });

    if (!built) {
      throw new Error('Match not found');
    }
    if (built.match.finished) {
      return { success: true }; // Silently succeed — match is done
    }

    await applyMatchFieldUpdate(ref, built);

    return { success: true };
  } catch (error) {
    console.error('Error updating live score:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Starts a match - marks it as started in Firebase
 * Only needs to be called by one player
 *
 * @param {object} params - Start match parameters
 * @param {string} params.tournamentId - Tournament ID
 * @param {number} params.matchIndex - Match index in bracket
 * @param {string} [params.instanceDate] - Instance date for recurring tournaments
 * @param {string} [params.token] - Player's scoring token (if present, uses Cloud Function)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function startMatch({ tournamentId, matchIndex, instanceDate, token }) {
  try {
    // If token is provided, use Cloud Function (player scoring - unauthenticated)
    if (token) {
      const result = await playerStartMatchFn({
        tournamentId,
        matchIndex,
        token,
        instanceDate
      });
      return result.data;
    }

    // Otherwise use direct Firestore write (director - authenticated)
    const { ref, data: source } = await loadBracketContainer(tournamentId, instanceDate);
    if (!source) {
      throw new Error('Tournament not found');
    }

    const built = buildMatchFieldUpdate(source, matchIndex, {
      started: true,
      actualStartTime: Date.now()
    });

    if (!built) {
      throw new Error('Match not found');
    }
    if (!built.match.started) {
      await applyMatchFieldUpdate(ref, built);
    }

    return { success: true };
  } catch (error) {
    console.error('Error starting match:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Subscribes to match status (started, finished, etc.)
 *
 * @param {string} tournamentId - Tournament ID
 * @param {number} matchIndex - Match index
 * @param {string} [instanceDate] - Instance date for recurring tournaments
 * @param {Function} callback - Callback function (receives { started, finished, winner })
 * @returns {Function} Unsubscribe function
 */
export function subscribeToMatchStatus(tournamentId, matchIndex, instanceDate, callback) {
  // Subscribe to the occurrence's own document for recurring tournaments so a
  // score update only re-sends that doc, not the whole parent.
  const ref = bracketContainerRef(tournamentId, instanceDate);

  return onSnapshot(ref, (snapshot) => {
    if (!snapshot.exists()) {
      callback({ started: false, finished: false });
      return;
    }

    const source = snapshot.data();
    const bracket = readBracketAsArray(source);

    if (!bracket || !bracket[matchIndex]) {
      callback({ started: false, finished: false });
      return;
    }

    const match = bracket[matchIndex];
    callback({
      started: !!match.started,
      finished: !!match.finished,
      winner: match.winner || null,
      score1: match.score1,
      score2: match.score2,
      actualStartTime: match.actualStartTime || match.startTime || null,
      // Include player-specific live games for restoration on page refresh
      p1LiveGames: match.p1LiveGames || null,
      p2LiveGames: match.p2LiveGames || null
    });
  });
}

/**
 * Gets pending score status for a match (legacy - kept for potential debugging)
 *
 * @param {string} tournamentId - Tournament ID
 * @param {number} matchIndex - Match index
 * @returns {Promise<{ p1Submitted: boolean, p2Submitted: boolean, retryCount?: number }>}
 */
export async function getPendingScoreStatus(tournamentId, matchIndex) {
  try {
    const p1Ref = doc(db, 'tournaments', tournamentId, 'pendingScores', `match_${matchIndex}_p1`);
    const p2Ref = doc(db, 'tournaments', tournamentId, 'pendingScores', `match_${matchIndex}_p2`);

    const [p1Doc, p2Doc] = await Promise.all([
      getDoc(p1Ref),
      getDoc(p2Ref)
    ]);

    // Check for retry count in tournament match data
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const tournamentDoc = await getDoc(tournamentRef);
    const tournament = tournamentDoc.data();
    const match = readBracketAsArray(tournament)?.[matchIndex];

    return {
      p1Submitted: p1Doc.exists(),
      p2Submitted: p2Doc.exists(),
      retryCount: match?.scoreRetryCount || 0
    };
  } catch (error) {
    console.error('Error getting pending score status:', error);
    return { p1Submitted: false, p2Submitted: false };
  }
}

/**
 * Clears pending scores for a match (used after reconciliation)
 *
 * @param {string} tournamentId - Tournament ID
 * @param {number} matchIndex - Match index
 */
export async function clearPendingScores(tournamentId, matchIndex) {
  try {
    const p1Ref = doc(db, 'tournaments', tournamentId, 'pendingScores', `match_${matchIndex}_p1`);
    const p2Ref = doc(db, 'tournaments', tournamentId, 'pendingScores', `match_${matchIndex}_p2`);

    await Promise.all([
      deleteDoc(p1Ref),
      deleteDoc(p2Ref)
    ]);
  } catch (error) {
    console.error('Error clearing pending scores:', error);
  }
}

/**
 * Deletes all pending scores for a tournament
 * Called when a tournament is reset to clean up old pending score records
 *
 * @param {string} tournamentId - Tournament ID
 * @returns {Promise<{ success: boolean, deleted: number, error?: string }>}
 */
export async function deleteAllPendingScores(tournamentId) {
  try {
    if (!tournamentId) {
      return { success: false, deleted: 0, error: 'Tournament ID required' };
    }

    const pendingScoresRef = collection(db, 'tournaments', tournamentId, 'pendingScores');
    const snapshot = await getDocs(pendingScoresRef);

    if (snapshot.empty) {
      return { success: true, deleted: 0 };
    }

    // Delete all pending score documents
    const deletePromises = snapshot.docs.map(docSnapshot => deleteDoc(docSnapshot.ref));
    await Promise.all(deletePromises);

    return { success: true, deleted: snapshot.size };
  } catch (error) {
    console.error('Error deleting all pending scores:', error);
    return { success: false, deleted: 0, error: error.message };
  }
}

/**
 * Gets all pending scores for a tournament (legacy - kept for potential debugging)
 *
 * @param {string} tournamentId - Tournament ID
 * @returns {Promise<Map<number, { p1?: object, p2?: object }>>}
 */
export async function getAllPendingScores(tournamentId) {
  try {
    const pendingScoresRef = collection(db, 'tournaments', tournamentId, 'pendingScores');
    const snapshot = await getDocs(pendingScoresRef);

    const scoresByMatch = new Map();

    snapshot.forEach(doc => {
      const data = doc.data();
      const matchIndex = data.matchIndex;

      if (!scoresByMatch.has(matchIndex)) {
        scoresByMatch.set(matchIndex, {});
      }

      const matchScores = scoresByMatch.get(matchIndex);
      matchScores[data.playerPosition] = {
        games: data.games,
        finalScore: data.finalScore,
        submittedAt: data.submittedAt
      };
    });

    return scoresByMatch;
  } catch (error) {
    console.error('Error getting all pending scores:', error);
    return new Map();
  }
}

/**
 * Finalizes a match in the tournament bracket
 * This updates the match with the final score and marks it as finished
 *
 * @param {string} tournamentId - Tournament ID
 * @param {number} matchIndex - Match index
 * @param {object} finalScore - Final score { p1: number, p2: number }
 * @param {number[]} [games] - Array of game results (1 = p1 won, 2 = p2 won) for match history
 * @param {string} [instanceDate] - Instance date for recurring tournaments
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function finalizeMatchFromScoring(tournamentId, matchIndex, finalScore, games, instanceDate) {
  try {
    const { ref, data: source } = await loadBracketContainer(tournamentId, instanceDate);
    if (!source) {
      throw new Error('Tournament not found');
    }

    // Peek at the match to determine the winner before building the update.
    const peek = buildMatchFieldUpdate(source, matchIndex, {});
    if (!peek) {
      throw new Error('Match not found');
    }
    if (peek.match.finished) {
      return { success: false, error: 'Match already completed' };
    }

    const match = peek.match;
    const winner = finalScore.p1 > finalScore.p2
      ? (match.player1 || match.p1?.name)
      : (match.player2 || match.p2?.name);

    const fieldChanges = {
      score1: String(finalScore.p1),
      score2: String(finalScore.p2),
      finished: true,
      winner,
      status: '',
      actualEndTime: Date.now(),
      selfScored: true,
      scoreRetryCount: 0,
      // Clear both legacy shared fields and new player-specific fields
      liveScore: null,
      liveGames: null,
      p1LiveScore: null,
      p2LiveScore: null,
      p1LiveGames: null,
      p2LiveGames: null,
      liveUpdatedAt: null,
      ...(games && games.length > 0 ? { games } : {})
    };

    const built = buildMatchFieldUpdate(source, matchIndex, fieldChanges);
    if (!built) {
      throw new Error('Match not found');
    }
    await applyMatchFieldUpdate(ref, built);

    await clearPendingScores(tournamentId, matchIndex);

    return { success: true, winner };
  } catch (error) {
    console.error('Error finalizing match from scoring:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Director corrects a match score (legacy function - kept for potential use)
 * Can be used if a player submitted an incorrect score
 *
 * @param {string} tournamentId - Tournament ID
 * @param {number} matchIndex - Match index
 * @param {object} finalScore - Director-set final score { p1: number, p2: number }
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function resolveScoreConflict(tournamentId, matchIndex, finalScore, instanceDate) {
  try {
    const { ref, data: source } = await loadBracketContainer(tournamentId, instanceDate);
    if (!source) {
      throw new Error('Tournament not found');
    }

    const built = buildMatchFieldUpdate(source, matchIndex, {
      scoreConflict: false,
      scoreRetryCount: 0,
      directorResolved: true,
      resolvedScore: finalScore,
      resolvedAt: new Date().toISOString()
    });

    if (!built) {
      throw new Error('Match not found');
    }
    await applyMatchFieldUpdate(ref, built);
    await clearPendingScores(tournamentId, matchIndex);

    return { success: true };
  } catch (error) {
    console.error('Error resolving score conflict:', error);
    return { success: false, error: error.message };
  }
}
